'use client';

import { useEffect, useRef } from 'react';
import { canRenderEnvironment } from './browser-capabilities.js';
import { scheduleSceneStartup } from './scene-startup.js';
import { preloadDeviceAssets } from './device-asset-downloads.js';

// Begin the scene request as soon as this client module is evaluated, before
// React effects/hydration scheduling. The server never imports browser code.
const earlyScene = typeof window !== 'undefined' && canRenderEnvironment() ? import('./blue-horizon.js') : null;
// The effect below reports failures; handle an unusually early rejection too.
void earlyScene?.catch(() => {});

type Props = {
  reducedMotion: boolean;
  onReady: () => void;
  onError: () => void;
};

export default function Environment({
  reducedMotion,
  onReady,
  onError,
}: Props) {
  const host = useRef<HTMLDivElement>(null);
  const control = useRef({ paused: false, reducedMotion });
  useEffect(() => {
    control.current = { paused: false, reducedMotion };
  }, [reducedMotion]);
  useEffect(() => {
    const element = host.current;
    if (!element) return;
    if (!earlyScene) { onError(); return; }
    let disposed = false;
    let cleanup: (() => void) | undefined;
    const loadingTimeout = window.setTimeout(() => { if (!disposed) onError(); }, 45000);
    // Prioritize the scene bundle over large device-image downloads.
    // Once its first frame is ready, downloads overlap the camera intro.
    const sceneModule = earlyScene;
    const cancelStartup = scheduleSceneStartup(() => {
    sceneModule
      .then(({ createBlueHorizon }) => {
        if (disposed) return;
        const mobile = Math.min(innerWidth, innerHeight) <= 700 || matchMedia('(pointer: coarse)').matches;
        // Reserve startup bandwidth for the first phone; later devices warm
        // ahead of their chapters after essential readiness.
        preloadDeviceAssets(['iphone-reference.glb']);
        if (mobile)
          window.dispatchEvent(new CustomEvent('codex-loading-progress', {detail: 25}));
        cleanup = createBlueHorizon({
          host: element,
          getControls: () => control.current,
          onReady: () => { window.clearTimeout(loadingTimeout); element.dataset.sceneReadyMs = String(Math.round(performance.now())); onReady(); },
          onError,
        });
      })
      .catch((error) => {
        console.error('Codex environment initialization failed', error);
        if (!disposed) onError();
      });
    });
    return () => {
      disposed = true;
      window.clearTimeout(loadingTimeout);
      cancelStartup();
      cleanup?.();
    };
  }, [onReady, onError]);
  return <div ref={host} className="three-environment" aria-hidden="true" />;
}
